import escapeStringRegexp from 'escape-string-regexp';
import { FormTemplateValidator, FormTemplateValidatorRegexEntries } from 'data/template/template.types';
import i18n from 'i18n';

const REGEX_TYPES = [
  'letters',
  'digits',
  'hexadecimals',
  'spaces',
];

const HIDDEN_REGEX_TYPES = [
  'stars',
];

const REGEX_WORDS = [
  'forever',
];

export default class RegexpValidator {
  private static TYPE = 'regexp';

  public static isType(type: string): boolean {
    return RegexpValidator.TYPE === type;
  }

  public static isValid(
    _propFields: unknown,
    _stateFields: unknown,
    value: string,
    _lastSavedValue: unknown,
    validator: FormTemplateValidator,
    _form: unknown,
  ): boolean {
    let { regexp } = validator;

    if (regexp == null) {
      return true;
    }

    if (Array.isArray(regexp)) {
      regexp = RegexpValidator.getRegexFromArray(regexp);
    }

    return (new RegExp(regexp)).test(value);
  }

  public static getErrorMessage(validator: FormTemplateValidator): string | undefined {
    const { regexp, i18nErrorMessageKey } = validator;
    let { errorMessage } = validator;

    if (Array.isArray(regexp)) {
      errorMessage = RegexpValidator.getLocalizedErrorMessage(regexp);
    }

    if (i18nErrorMessageKey != null) {
      errorMessage = i18n.t(i18nErrorMessageKey);
    }

    return errorMessage;
  }

  private static getLocalizedErrorMessage(regexp: FormTemplateValidatorRegexEntries[]): string {
    // Extract the accepted types
    const types = regexp
      .filter((type) => REGEX_TYPES.includes(type))
      .map((type) => i18n.t(`common.form.fieldError.regexpType.${type}`))
      .join(', ');

    // Extract the accepted special chars
    const [specialChars] = regexp
      .filter((type) => ![...REGEX_TYPES, ...HIDDEN_REGEX_TYPES, ...REGEX_WORDS].includes(type));

    // Extract the words
    const words = regexp.filter((type) => REGEX_WORDS.includes(type)).join(', ');

    if (words) {
      return i18n.t('common.form.fieldError.regexpWithWord', { types, words, count: words.split(',').length });
    }

    if (specialChars) {
      return i18n.t('common.form.fieldError.regexpSpecialChars', { types, specialChars });
    }

    return i18n.t('common.form.fieldError.regexp', { types });
  }

  private static getRegexFromArray(regexp: FormTemplateValidatorRegexEntries[]): string {
    const characters: string[] = [];
    const groups: string[] = [];

    regexp.forEach((regexEntry) => {
      switch (regexEntry) {
        case 'letters':
          characters.push('A-Za-z');
          break;
        case 'hexadecimals':
          characters.push('a-fA-F0-9');
          break;
        case 'digits':
          characters.push('0-9');
          break;
        case 'spaces':
          characters.push('\\s');
          break;
        case 'stars':
          groups.push(`(${escapeStringRegexp('*****')})`);
          break;
        case 'forever':
          groups.push(`(${escapeStringRegexp('forever')})`);
          break;
        default:
          characters.push(escapeStringRegexp(regexEntry));
          break;
      }
    });

    const characterSet = characters.join('');
    const groupSet = groups.join('|');

    return `^((${groupSet})|([${characterSet}]*))$`;
  }
}
